import React, { useMemo } from "react";

import {
  MadFullFragment,
  MadReportFullFragment,
  UserMinimalFragment,
} from "../../graphql/fragments";
import useTranslations from "../../hooks/useTranslations";
import useInsertReportUser from "../../graphql/hooks/mutations/useInsertReportUser";

import { Avatar, Badge, Button } from "../wombat-ui";
import { GET_MAD } from "../../hooks/useGetMad";
import { formatDistanceToNow, isBefore } from "date-fns";
import { cloneDeep } from "lodash";
import useDateFnsLocale from "../../hooks/useDateFnsLocale";

type UserFilledActivityProps = {
  user: UserMinimalFragment;
  filledAt: string;
  skippedAt?: string;
  isTalent?: boolean;
  hasNext?: boolean;
};

const UserFilledActivity: React.FC<UserFilledActivityProps> = ({
  user,
  filledAt,
  skippedAt,
  isTalent = false,
  hasNext = false,
}) => {
  const t = useTranslations();
  const dateFnsLocale = useDateFnsLocale();

  return (
    <li>
      <div className="relative pb-8">
        {hasNext && (
          <span
            className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-light-500"
            aria-hidden="true"
          ></span>
        )}
        <div className="relative flex items-start space-x-3">
          <div>
            <div className="relative px-1">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-light-0 ring-8 ring-white">
                <Avatar
                  size={user.avatar ? 8 : 5}
                  type="user"
                  src={user.avatar}
                  alt={user.name}
                />
              </div>
            </div>
          </div>
          <div className="min-w-0 flex-1 py-1.5">
            <div className="flex items-center space-x-1 text-sm text-gray-500">
              {skippedAt && (
                <div className="w-2.5 h-2.5 rounded-full mr-2 bg-warning-500" />
              )}
              <a href="#" className={`font-medium text-dark-100`}>
                {user.name}
              </a>
              <span className={skippedAt ? "text-warning-600" : ""}>
                {skippedAt
                  ? t("mads.activities.omitted")
                  : t("mads.activities.hasFilled")}
              </span>
              <span className="whitespace-nowrap">
                {formatDistanceToNow(new Date(filledAt), {
                  addSuffix: true,
                  locale: dateFnsLocale,
                })}
              </span>
              <Badge scale="sm" variant={isTalent ? "secondary" : "warning"}>
                {isTalent ? t("mads.talent") : t("mads.referent", { count: 0 })}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

type UserNotFilledActivityProps = {
  mad: MadFullFragment;
  report: MadReportFullFragment;
  user: UserMinimalFragment;
  isTalent?: boolean;
  hasNext?: boolean;
};

const UserNotFilledActivity: React.FC<UserNotFilledActivityProps> = ({
  mad,
  report,
  user,
  isTalent = false,
  hasNext = false,
}) => {
  const t = useTranslations();

  const [skipUser] = useInsertReportUser({
    refetchQueries: [{ query: GET_MAD, variables: { id: mad.id } }],
  });

  return (
    <li>
      <div className="relative pb-8">
        {hasNext && (
          <span
            className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-light-500"
            aria-hidden="true"
          ></span>
        )}
        <div className="relative flex items-start space-x-3">
          <div>
            <div className="relative px-1">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-light-0 ring-8 ring-white">
                <Avatar
                  size={user.avatar ? 8 : 5}
                  type="user"
                  src={user.avatar}
                  alt={user.name}
                />
              </div>
            </div>
          </div>
          <div className="min-w-0 flex-1 py-1.5">
            <div className="flex flex-col justify-between space-y-1 text-sm text-gray-500">
              <div className="flex space-x-1 text-sm text-gray-500">
                <a href="#" className={"font-medium text-dark-100"}>
                  {user.name}
                </a>
                <span>{t("mads.activities.awaiting")}</span>
                <Badge scale="sm" variant={isTalent ? "secondary" : "warning"}>
                  {isTalent
                    ? t("mads.talent")
                    : t("mads.referent", { count: 0 })}
                </Badge>
              </div>
              {!isTalent && (
                <div>
                  <Button
                    variant="subtle"
                    scale="xs"
                    onClick={() => {
                      if (confirm(t("reports.skipConfirm") as string)) {
                        skipUser({
                          variables: {
                            input: {
                              reportId: report.id,
                              userId: user.id,
                              skippedAt: new Date().toISOString(),
                              filledAt: new Date().toISOString(),
                            },
                          },
                        });
                      }
                    }}
                  >
                    {t("mads.skipUser")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

type ReportCheckedActivityProps = {
  mad: MadFullFragment;
  report: MadReportFullFragment;
  hasNext?: boolean;
};

const ReportCheckedActivity: React.FC<ReportCheckedActivityProps> = ({
  mad,
  report,
  hasNext,
}) => {
  const dateFnsLocale = useDateFnsLocale();
  const t = useTranslations();

  return (
    <>
      <li id={`report_${report.id}_checked_activity`}>
        <div className="relative pb-8">
          <span
            className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
            aria-hidden="true"
          ></span>
          <div className="relative flex items-start space-x-3">
            <Avatar
              reverse
              size={10}
              className="ring-8 ring-white"
              type="host"
              alt="Report"
            />
            <div className="flex-1 min-w-0">
              <div>
                <div className="text-sm">
                  <a href="#" className="font-medium text-dark-100">
                    {t(`reports.header.${report.type}`)} -{" "}
                    {mad.title ? mad.title : mad.template?.title}
                  </a>
                </div>
                <p className="mt-0.5 text-sm text-light-900">
                  {t("mads.activities.completed")}{" "}
                  {formatDistanceToNow(new Date(report.checkedAt), {
                    addSuffix: true,
                    locale: dateFnsLocale,
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </li>
      {report.reportReferents.map((rr) => (
        <UserFilledActivity
          key={rr.id}
          filledAt={rr.filledAt}
          skippedAt={rr.skippedAt}
          user={rr.referent}
          hasNext
        />
      ))}
      <UserFilledActivity
        isTalent
        filledAt={report.filledAt}
        user={mad.talent}
        hasNext={hasNext}
      />
    </>
  );
};

type ReportUncheckedActivityProps = {
  mad: MadFullFragment;
  report: MadReportFullFragment;
  hasNext?: boolean;
};

const ReportUncheckedActivity: React.FC<ReportUncheckedActivityProps> = ({
  mad,
  report,
  hasNext = false,
}) => {
  const t = useTranslations();
  const dateFnsLocale = useDateFnsLocale();

  return (
    <>
      <li id={`report_${report.id}_checked_activity`}>
        <div className="relative pb-8">
          <span
            className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
            aria-hidden="true"
          ></span>
          <div className="relative flex items-start space-x-3">
            <Avatar
              reverse
              size={10}
              className="ring-8 ring-white"
              type="host"
              alt="Report"
            />
            <div className="flex-1 min-w-0">
              <div>
                <div className="text-sm">
                  <a href="#" className="font-medium text-dark-100">
                    {t(`reports.header.${report.type}`)} -{" "}
                    {mad.title ? mad.title : mad.template?.title}
                  </a>
                </div>
                <p className="mt-0.5 text-sm text-light-900">
                  {t("mads.activities.toStart")}{" "}
                  {formatDistanceToNow(new Date(report.createdAt), {
                    addSuffix: true,
                    locale: dateFnsLocale,
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </li>
      {report.reportReferents.map((rr) =>
        rr.filledAt ? (
          <UserFilledActivity
            filledAt={rr.filledAt}
            user={rr.referent}
            hasNext={hasNext}
          />
        ) : (
          <UserNotFilledActivity
            key={rr.id}
            user={rr.referent}
            report={report}
            mad={mad}
            hasNext
          />
        )
      )}
      {report.filledAt ? (
        <UserFilledActivity
          isTalent
          filledAt={report.filledAt}
          user={mad.talent}
          hasNext={hasNext}
        />
      ) : (
        <UserNotFilledActivity
          isTalent
          user={mad.talent}
          hasNext={hasNext}
          report={report}
          mad={mad}
        />
      )}
    </>
  );
};

type UserFollowUpActivitiesProps = {
  mad: MadFullFragment;
};

const UserFollowUpActivities: React.FC<UserFollowUpActivitiesProps> = ({
  mad,
}) => {
  const t = useTranslations();

  const orderedReports = useMemo(() => {
    return cloneDeep(mad.reports).sort((lhs, rhs) =>
      isBefore(new Date(lhs.createdAt), new Date(rhs.createdAt)) ? 1 : -1
    );
  }, [mad]);

  if (!mad) {
    return null;
  }

  return (
    <div className="flex flex-col">
      {orderedReports.length === 0 && (
        <div className="flex justify-center text-sm text-light-900">
          {t("mads.noActivity")}
        </div>
      )}
      {orderedReports.length > 0 && (
        <ul className="-mb-8">
          {orderedReports.map((report, index) => {
            if (report.checkedAt)
              return (
                <ReportCheckedActivity
                  key={report.id}
                  report={report}
                  mad={mad}
                  hasNext={index + 1 < orderedReports.length}
                />
              );
            return (
              <ReportUncheckedActivity
                key={report.id}
                report={report}
                mad={mad}
                hasNext={index + 1 < orderedReports.length}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default UserFollowUpActivities;
